import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Alert, CircularProgress, Stack, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ExpenseType } from '../../../types/homeExpenses';
import { useBudgets } from '../../../hooks/useBudgets';
import { formatEuro } from '../../../utils/currency';
import { useExpensesYear } from '../../../context/ExpensesYearContext';

const formatCurrency = formatEuro;

const BudgetsPage: React.FC = () => {
  const { year } = useExpensesYear();
  const { budgets, loading, error, saving, refresh, updateBudget, saveBudgets, type, setType } = useBudgets(ExpenseType.Home);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  useEffect(() => {
    // Fetch budgets when year or type changes
    refresh();
    // Do not include `refresh` in deps to avoid refiring every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, type]);

  const sortedBudgets = useMemo(() => {
    return [...budgets].sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, [budgets]);

  const handleBudgetChange = (categoryPublicId: string, val: string) => {
    const num = Number(val);
    updateBudget(categoryPublicId, isNaN(num) ? 0 : Math.max(0, num));
    setDirty(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      await saveBudgets();
      setDirty(false);
      setSaveSuccess(true);
    } catch (e: any) {
      setSaveError(e?.message || 'Falha ao guardar os orçamentos');
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Orçamentos por categoria – {year}</Typography>
        <ToggleButtonGroup exclusive size="small" value={type} onChange={(_, v) => v !== null && setType(v)}>
          <ToggleButton value={ExpenseType.Home}>Casa</ToggleButton>
          <ToggleButton value={ExpenseType.Personal}>Pessoal</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>A carregar orçamentos…</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <>
            {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
            {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Orçamentos guardados</Alert>}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cor</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Média do ano anterior</TableCell>
                    <TableCell align="right">Orçamento (€ / mês)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedBudgets.map(b => (
                    <TableRow key={b.categoryPublicId} hover>
                      <TableCell width={100}><Chip size="small" label=" " sx={{ bgcolor: b.categoryColor || '#9e9e9e', color: b.categoryColor ? 'white' : 'inherit' }} /></TableCell>
                      <TableCell>{b.categoryName}</TableCell>
                      <TableCell align="right">{formatCurrency(b.previousYearAverageAmount || 0)}</TableCell>
                      <TableCell align="right" width={220}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: 1, min: 0 }}
                          value={b.monthlyBudgetAmount ?? 0}
                          onChange={(e) => handleBudgetChange(b.categoryPublicId, e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => refresh()} disabled={loading || saving}>Atualizar</Button>
              <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>{saving ? 'A guardar…' : 'Guardar orçamentos'}</Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BudgetsPage;
